import React, { useState, useEffect } from 'react';
import styles from './Pagination.module.css';

interface PaginationProps {
  currentPage: number;
  totalRecords: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  showPageSize?: boolean;
}

const range = (from: number, to: number, step = 1) => {
  let i = from;
  const range = [];
  while (i <= to) {
    range.push(i);
    i += step;
  }
  return range;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 30, 40, 50],
  showPageSize = true,
}) => {
  const totalPages = Math.ceil(totalRecords / pageSize);

  if (totalRecords === 0) {
    return null;
  }

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onPageSizeChange(Number(e.target.value));
  };

  const getPageNumbers = () => {
    const blockSize = 10;
    const currentBlockStart = Math.floor((currentPage - 1) / blockSize) * blockSize + 1;
    const currentBlockEnd = Math.min(currentBlockStart + blockSize - 1, totalPages);

    const pages: (string | number)[] = range(currentBlockStart, currentBlockEnd);

    if (currentBlockStart > 1) {
      pages.unshift('PREV_DOTS');
    }
    if (currentBlockEnd < totalPages) {
      pages.push('NEXT_DOTS');
    }

    return { pages, currentBlockStart, currentBlockEnd };
  };

  const { pages, currentBlockStart, currentBlockEnd } = getPageNumbers();

  const handlePageClick = (page: number | string) => {
    if (page === 'PREV_DOTS') {
      const prevPage = Math.max(1, currentBlockStart - 1);
      onPageChange(prevPage);
    } else if (page === 'NEXT_DOTS') {
      const nextPage = Math.min(totalPages, currentBlockEnd + 1);
      onPageChange(nextPage);
    } else if (typeof page === 'number') {
      onPageChange(page);
    }
  };

  return (
    <div className={styles.paginationContainer}>
      {showPageSize && (
        <div className={styles.pageSizeContainer}>
          <span className={styles.pageSizeLabel}>Page Size:</span>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className={styles.pageSizeSelect}
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}

      {totalPages > 0 && (
        <ul className={styles.pagination}>
          {pages.map((page, index) => {
            const isDots = page === 'PREV_DOTS' || page === 'NEXT_DOTS';
            
            return (
              <li key={`${page}-${index}`} className={styles.pageItem}>
                <button
                  onClick={() => handlePageClick(page)}
                  className={`${styles.pageLink} ${currentPage === page ? styles.active : ''} ${isDots ? styles.dots : ''}`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {isDots ? '...' : page}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Pagination;
